import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAuth } from '../auth/useAuth'
import { useI18n } from '../i18n/useI18n'
import { disputeMetric, getMyContractorIntelligence } from './contractor-intelligence-service'

export function ContractorIntelligenceDashboard() {
  const { user } = useAuth()
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [statement, setStatement] = useState('')
  const query = useQuery({ queryKey: ['contractor-intelligence', user?.id], queryFn: getMyContractorIntelligence, enabled: Boolean(user) })
  const dispute = useMutation({
    mutationFn: () => disputeMetric('reliability_score', statement),
    onSuccess: async () => {
      setStatement('')
      await queryClient.invalidateQueries({ queryKey: ['contractor-intelligence', user?.id] })
    },
  })

  if (query.isLoading) return <p>{t('Loading contractor intelligence…')}</p>
  if (query.error) return <div className="form-error">{query.error.message}</div>
  const data = query.data!
  const hasReliableScore = data.enabled && !data.newProvider && data.score !== null

  return <section className="intelligence-dashboard" aria-labelledby="intelligence-title">
    <div className="marketplace-status-row">
      <div>
        <p className="eyebrow">AMIGO Contractor Intelligence™</p>
        <h2 id="intelligence-title">{t('Reliability Score')}</h2>
        <div><span className="status-pill">{t('Private pilot')}</span> <span className="status-pill">{t('Not visible to customers')}</span></div>
      </div>
      <strong className="intelligence-score">{hasReliableScore ? Math.round(data.score ?? 0) : t('Insufficient data')}</strong>
    </div>
    <p>{t('Confidence')}: <strong>{t(data.confidence.replaceAll('_', ' '))}</strong> · {data.evidenceCount} {t('verified jobs')}</p>
    {!data.enabled && <p className="legal-warning">{t('Your private improvement dashboard is not enabled yet. Metrics remain available only to authorized administrators during validation.')}</p>}
    {data.enabled && !hasReliableScore && <p className="legal-warning">{t('Insufficient verified history. No reliability score is shown until the minimum evidence threshold is met.')}</p>}
    <div className="intelligence-metrics">
      <div><span>{t('Score trend')}</span><strong>{data.trend === null ? '—' : `${data.trend >= 0 ? '+' : ''}${data.trend.toFixed(1)}`}</strong></div>
      <div><span>{t('Category performance')}</span><strong>{data.categories.length}</strong></div>
      <div><span>{t('Open metric disputes')}</span><strong>{data.disputes.length}</strong></div>
    </div>
    <h3>{t('Improvement guidance')}</h3>
    <ul>{data.improvements.map(item => <li key={item}>{t(item)}</li>)}</ul>
    <details>
      <summary>{t('Dispute a metric')}</summary>
      <p>{t('Tell us which verified event appears inaccurate. A dispute does not imply fault and will be reviewed with its audit history.')}</p>
      <textarea value={statement} onChange={event => setStatement(event.target.value)} minLength={20} maxLength={4000} />
      <button disabled={statement.trim().length < 20 || dispute.isPending} onClick={() => dispute.mutate()}>{t('Submit metric dispute')}</button>
      {dispute.isSuccess && <p className="form-success">{t('Dispute submitted for review.')}</p>}
    </details>
  </section>
}
